import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { BikeProvider } from './context/BikeContext';
import { WishlistProvider } from './context/WishlistContext';
import Navbar from './components/Navbar';
import { Footer } from './components/Footer';
import AuthForm from './components/AuthForm';
import DeliveryStatusBar from './components/DeliveryStatusBar';
import { InstagramReelsButton } from './components/InstagramReelsButton';
import ReelsOnboardingHint from './components/ReelsOnboardingHint';
import AutoLoginPopup from './components/AutoLoginPopup';
import { useAuthModal } from './context/AuthModalContext';
import { trackWebsiteVisit } from './utils/analytics';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import About from './pages/About';
import AboutOutrans from './pages/AboutOutrans';
import Contact from './pages/Contact';
import Wishlist from './pages/Wishlist';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Refund from './pages/Refund';
import Shipping from './pages/Shipping';
import TankCover from './pages/TankCover';

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/918793678780"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-40 group"
      aria-label="Contact us on WhatsApp"
    >
      <span
        className="relative flex items-center justify-center rounded-full text-white overflow-hidden transition-all duration-300 group-hover:scale-105 active:scale-95"
        style={{
          width: '54px',
          height: '54px',
          backgroundColor: '#25D366',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        }}
      >
        {/* Official WhatsApp Icon */}
        <svg 
          viewBox="0 0 24 24" 
          fill="currentColor"
          className="w-6 h-6"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </span>
    </a>
  );
};

function AppContent() {
  const location = useLocation();
  const { isOpen, initialView, closeAuth } = useAuthModal();

  // Track website visit on first load
  useEffect(() => {
    trackWebsiteVisit();
  }, []);

  const shouldHideFooter =
    location.pathname === '/cart' ||
    location.pathname === '/checkout';
  const shouldShowDeliveryStatus =
    location.pathname === '/' ||
    location.pathname === '/orders';

  return (
    <div className="App min-h-screen bg-[#030804] flex flex-col">
      <Navbar />

      <div className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/about" element={<About />} />
          <Route path="/about-outrans" element={<AboutOutrans />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/tank-cover" element={<TankCover />} />
        </Routes>
      </div>

      {!shouldHideFooter && <Footer />}

      <AuthForm
        isOpen={isOpen}
        onClose={closeAuth}
        initialView={initialView}
      />

      {shouldShowDeliveryStatus && <DeliveryStatusBar />}

      {/* Reels Onboarding Hint - Only shows on home page */}
      <ReelsOnboardingHint />

      {/* Auto Login Popup - Only shows on home page for non-logged-in users */}
      <AutoLoginPopup />

      {/* Floating Instagram Reels — above WhatsApp */}
      <InstagramReelsButton />

      {/* Global WhatsApp Button */}
      <WhatsAppButton />

      <Toaster
        position="top-right"
        duration={1000}
        toastOptions={{
          style: {
            background: '#0a1a0f',
            color: '#FAFAFA',
            border: '1px solid #1a4d2e',
            fontFamily: 'Cormorant Garamond, serif',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <AuthModalProvider>
              <BikeProvider>
                <AppContent />
              </BikeProvider>
            </AuthModalProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
